import React from "react";
import { toast } from "sonner";

import { ClientFactory } from "@/main/common/ClientFactory";
import { getViewerActionRecord, rememberViewerActionRecord } from "@/main/common/ViewerActionRecords";
import type { Localizer } from "@/main/react/Types";
import logger from "@/lib/logger";

/**
 * Mounted `useDiscussionFollow` instances, by post, so a follow confirmed OUTSIDE the hook (the
 * reply-published toast) reaches the post's pill without a reload. A plain listener set, not a
 * `useSyncExternalStore` store: it carries a server-confirmed value to a hook that already keeps its own
 * state, so rule 31 does not bind it.
 */
const followConfirmedListeners = new Map<string, Set<(following: boolean) => void>>();

export type DiscussionFollowController = {
    /** The viewer follows this post. */
    following: boolean;
    /** False until the server has answered once, or this tab already holds a record. */
    known: boolean;
    /** A follow or unfollow is in flight. */
    pending: boolean;
    toggle: () => Promise<void>;
};

/**
 * Follow a post (question loop, Part A): the one state behind the post's Follow pill and the phone menu's
 * Follow / Stop following item. Design: `designs/01-now/2026-09-10-question-loop-follow-and-ask-design.md`.
 *
 * - **One state, two controls.** `DiscussionButtons` renders the pill from `sm` up and the menu item below
 *   it (the Share pattern), and both read this hook, so the two can never disagree.
 * - **Recorded in `ViewerActionRecords`** (rule 33), and written there ONLY from a server answer: the first
 *   read, and the response to a follow or unfollow. A Back-restored page seeds from what this tab last saw
 *   confirmed instead of re-asking, and the notification row's "Stop following" writes the same record.
 *   The optimistic flip lives in local state alone, via `resolveViewerActionRecord`'s documented
 *   alternative to `useViewerActionRecord`: that hook's setter compares against the last RENDERED value,
 *   so an optimistic flip and its revert with no render between them would drop the revert.
 * - **No DTO carries the state**, so the first read is a GET, made only when `enabled` (the full discussion
 *   page, a logged-in viewer who is not the author, a live post). A feed card passes `enabled = false` and
 *   costs nothing.
 * - **Replying does not follow** (the automatic follow was removed 2026-09-12), so a published reply is
 *   no reason to re-read the state; only the toggle, the notification row's "Stop following" and the
 *   reply-published toast's "Follow this post" (`followDiscussionFromReplyToast`, QoL D7) change it.
 */
export function useDiscussionFollow(
    discussionPublicId: string,
    localizer: Localizer,
    enabled: boolean,
): DiscussionFollowController {
    const [following, setFollowingState] = React.useState<boolean>(
        () => getViewerActionRecord("discussion-follow", discussionPublicId) ?? false,
    );
    const [known, setKnown] = React.useState(
        () => getViewerActionRecord("discussion-follow", discussionPublicId) !== undefined,
    );
    const [pending, setPending] = React.useState(false);

    // Read by the toggle, and kept current by every write rather than by the next render, so a revert
    // right after an optimistic flip always knows what it is reverting from.
    const followingRef = React.useRef(following);
    const pendingRef = React.useRef(false);

    /** A value that is only this tab's guess. Never recorded. */
    const showFollowing = React.useCallback((next: boolean) => {
        followingRef.current = next;
        setFollowingState(next);
    }, []);

    /** A value the server confirmed. Recorded, so it outlives the control. */
    const confirmFollowing = React.useCallback((next: boolean) => {
        rememberViewerActionRecord("discussion-follow", discussionPublicId, next);
        showFollowing(next);
        setKnown(true);
    }, [discussionPublicId, showFollowing]);

    // A follow taken elsewhere on this page (the reply-published toast) lands on this pill too.
    React.useEffect(() => {
        if (!discussionPublicId) {
            return undefined;
        }
        const listener = (next: boolean) => {
            showFollowing(next);
            setKnown(true);
        };
        let listeners = followConfirmedListeners.get(discussionPublicId);
        if (!listeners) {
            listeners = new Set();
            followConfirmedListeners.set(discussionPublicId, listeners);
        }
        listeners.add(listener);
        return () => {
            listeners.delete(listener);
            if (listeners.size === 0) {
                followConfirmedListeners.delete(discussionPublicId);
            }
        };
    }, [discussionPublicId, showFollowing]);

    // The same instance handed a different post re-seeds from THAT post's record.
    React.useEffect(() => {
        const recorded = getViewerActionRecord("discussion-follow", discussionPublicId);
        showFollowing(recorded ?? false);
        setKnown(recorded !== undefined);
    }, [discussionPublicId, showFollowing]);

    const refresh = React.useCallback(async () => {
        try {
            const { data } = await ClientFactory.getDiscussionFollowState(discussionPublicId);
            confirmFollowing(Boolean(data?.following));
        } catch (error) {
            logger.warn("[useDiscussionFollow] follow state read failed", error);
            setKnown(true);
        }
    }, [discussionPublicId, confirmFollowing]);

    React.useEffect(() => {
        if (!enabled || !discussionPublicId) {
            return;
        }
        if (getViewerActionRecord("discussion-follow", discussionPublicId) === undefined) {
            void refresh();
        }
    }, [enabled, discussionPublicId, refresh]);

    const toggle = React.useCallback(async () => {
        if (!enabled || pendingRef.current || !discussionPublicId) {
            return;
        }
        const wasFollowing = followingRef.current;
        pendingRef.current = true;
        setPending(true);
        showFollowing(!wasFollowing);
        try {
            const { data } = wasFollowing
                ? await ClientFactory.unfollowDiscussion(discussionPublicId)
                : await ClientFactory.followDiscussion(discussionPublicId);
            confirmFollowing(Boolean(data?.following));
            if (wasFollowing) {
                toast.success(localizer("typescript.discussion.follow_stopped_toast"));
            }
        } catch (error) {
            showFollowing(wasFollowing);
            logger.error("[useDiscussionFollow] follow update failed", error);
            toast.error(localizer("shared.action.failed"));
        } finally {
            pendingRef.current = false;
            setPending(false);
        }
    }, [enabled, discussionPublicId, localizer, showFollowing, confirmFollowing]);

    return { following, known, pending, toggle };
}

/**
 * Whether the reply-published toast offers "Follow this post" (QoL D7, 2026-09-26). Hidden when the
 * viewer wrote the post (`hostRelation` from the publish response must be exactly "cross"; "own" and an
 * absent field both fail closed) or already follows it per this tab's `discussion-follow` record. With no
 * record the offer shows: the follow PUT is idempotent, so a tap by a viewer who already followed is
 * harmless. Design: `designs/01-now/2026-09-10-question-loop-follow-and-ask-design.md` ("The reply-toast offer").
 */
export function shouldOfferFollowOnReplyToast(discussionPublicId: string, hostRelation: string | undefined): boolean {
    if (!discussionPublicId || hostRelation !== "cross") {
        return false;
    }
    return getViewerActionRecord("discussion-follow", discussionPublicId) !== true;
}

/**
 * The toast action's tap: follow tagged `surface=reply_toast` (the member taps, so `source` stays MANUAL),
 * record the server's answer in the shared `discussion-follow` record, update every mounted pill for the
 * post, and say what following means.
 */
export async function followDiscussionFromReplyToast(discussionPublicId: string, localizer: Localizer): Promise<void> {
    try {
        const { data } = await ClientFactory.followDiscussion(discussionPublicId, { surface: "reply_toast" });
        const following = Boolean(data?.following);
        rememberViewerActionRecord("discussion-follow", discussionPublicId, following);
        followConfirmedListeners.get(discussionPublicId)?.forEach((listener) => listener(following));
        if (following) {
            toast.success(localizer("typescript.discussion.follow_title"));
        }
    } catch (error) {
        logger.error("[useDiscussionFollow] reply-toast follow failed", error);
        toast.error(localizer("shared.action.failed"));
    }
}
